import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaThumbtack } from 'react-icons/fa';
import { api } from '../../../api/client';
import type { Work } from '../../../api/types';
import { Icon } from '../../../components/Icon';

type Draft = Partial<Work> & {
    tags: string[];
    imageUrls: string[];
    urls: string[];
};

function toDraft(w: Work): Draft {
    return {
        ...w,
        tags: [...(w.tags ?? [])],
        imageUrls: [...(w.imageUrls ?? [])],
        urls: [...(w.urls ?? [])],
    };
}

function fromDraft(d: Draft): Partial<Work> {
    return {
        slug: d.slug ?? '',
        title: d.title ?? '',
        description: d.description ?? '',
        date: d.date ?? '',
        tags: d.tags.map((t) => t.trim()).filter(Boolean),
        urls: d.urls.map((u) => u.trim()).filter(Boolean),
        imageUrls: d.imageUrls.filter(Boolean),
        pinnedOrder: typeof d.pinnedOrder === 'number' ? 1 : null,
    };
}

const EMPTY_DRAFT: Draft = {
    slug: '',
    title: '',
    description: '',
    date: '',
    tags: [],
    imageUrls: [],
    urls: [],
    pinnedOrder: null,
};

function TagInput({
    value,
    onChange,
    suggestions,
}: {
    value: string[];
    onChange: (next: string[]) => void;
    suggestions: string[];
}) {
    const [input, setInput] = useState('');
    const [open, setOpen] = useState(false);

    const normalized = input.trim().toLowerCase();
    const available = useMemo(() => {
        const taken = new Set(value.map((v) => v.toLowerCase()));
        return Array.from(new Set(suggestions))
            .filter((s) => !taken.has(s.toLowerCase()))
            .filter((s) => !normalized || s.toLowerCase().includes(normalized))
            .sort((a, b) => a.localeCompare(b));
    }, [suggestions, value, normalized]);

    const canCreate =
        !!normalized &&
        !suggestions.some((s) => s.toLowerCase() === normalized) &&
        !value.some((v) => v.toLowerCase() === normalized);

    const add = (tag: string) => {
        const t = tag.trim();
        if (!t) return;
        if (value.some((v) => v.toLowerCase() === t.toLowerCase())) {
            setInput('');
            return;
        }
        onChange([...value, t]);
        setInput('');
    };

    const removeAt = (i: number) => onChange(value.filter((_, idx) => idx !== i));

    return (
        <div className="admin-tag-input">
            <div className="admin-tag-chips">
                {value.map((tag, i) => (
                    <span key={`${tag}-${i}`} className="admin-tag-chip">
                        {tag}
                        <button
                            type="button"
                            onClick={() => removeAt(i)}
                            aria-label={`Remove ${tag}`}
                        >×</button>
                    </span>
                ))}
                <input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onFocus={() => setOpen(true)}
                    onBlur={() => setTimeout(() => setOpen(false), 120)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            if (input.trim()) add(input);
                        } else if (e.key === 'Backspace' && !input && value.length > 0) {
                            removeAt(value.length - 1);
                        }
                    }}
                    placeholder={value.length === 0 ? 'Add a tag…' : ''}
                />
            </div>
            {open && (available.length > 0 || canCreate) && (
                <div className="admin-tag-suggestions">
                    {available.map((s) => (
                        <button
                            type="button"
                            key={s}
                            className="admin-tag-suggestion"
                            onMouseDown={(e) => {
                                e.preventDefault();
                                add(s);
                            }}
                        >
                            {s}
                        </button>
                    ))}
                    {canCreate && (
                        <button
                            type="button"
                            className="admin-tag-suggestion is-create"
                            onMouseDown={(e) => {
                                e.preventDefault();
                                add(input);
                            }}
                        >
                            + Create “{input.trim()}”
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

export function WorksPanel() {
    const navigate = useNavigate();
    const location = useLocation();
    const [works, setWorks] = useState<Work[]>([]);
    const [editing, setEditing] = useState<Draft | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const load = () => api.works.list().then(setWorks).catch((e) => setError(e.message));

    useEffect(() => {
        load();
    }, []);

    // Sync editing state with the URL. /admin/works/new → new draft;
    // /admin/works/:id → the matching work; /admin → no editing.
    useEffect(() => {
        const m = location.pathname.match(/^\/admin\/works\/(new|\d+)$/);
        if (!m) {
            setEditing(null);
            return;
        }
        if (m[1] === 'new') {
            setEditing(EMPTY_DRAFT);
            return;
        }
        const id = Number(m[1]);
        const w = works.find((x) => x.id === id);
        if (w) setEditing(toDraft(w));
    }, [location.pathname, works]);

    const openEditor = (w: Work) => navigate(`/admin/works/${w.id}`);
    const openNewEditor = () => navigate('/admin/works/new');
    const closeEditor = () => navigate('/admin');

    const allTags = useMemo(() => {
        const set = new Set<string>();
        for (const w of works) for (const t of w.tags ?? []) if (t) set.add(t);
        return Array.from(set);
    }, [works]);

    // Mirror the public Works page ordering so the admin list matches it 1:1.
    const displayedWorks = useMemo(() => {
        return [...works].sort((a, b) => {
            const aPinned = typeof a.pinnedOrder === 'number';
            const bPinned = typeof b.pinnedOrder === 'number';
            if (aPinned && !bPinned) return -1;
            if (bPinned && !aPinned) return 1;
            return new Date(b.date).getTime() - new Date(a.date).getTime();
        });
    }, [works]);

    const save = async () => {
        if (!editing) return;
        setBusy(true);
        setError(null);
        try {
            const body = fromDraft(editing);
            if (editing.id) {
                await api.works.update(editing.id, body);
            } else {
                await api.works.create(body);
            }
            await load();
            closeEditor();
        } catch (e: any) {
            setError(e.message ?? 'Save failed');
        } finally {
            setBusy(false);
        }
    };

    const remove = async () => {
        if (!editing?.id) return;
        if (!window.confirm(`Delete "${editing.title}"?`)) return;
        setBusy(true);
        try {
            await api.works.remove(editing.id);
            await load();
            closeEditor();
        } catch (e: any) {
            setError(e.message ?? 'Delete failed');
        } finally {
            setBusy(false);
        }
    };

    const uploadImage = async (file: File) => {
        setBusy(true);
        try {
            const { url } = await api.images.upload(file);
            setEditing((d) => (d ? { ...d, imageUrls: [...d.imageUrls, url] } : d));
        } catch (e: any) {
            setError(e.message ?? 'Upload failed');
        } finally {
            setBusy(false);
        }
    };

    const removeImage = (index: number) => {
        setEditing((d) =>
            d ? { ...d, imageUrls: d.imageUrls.filter((_, i) => i !== index) } : d,
        );
    };

    const moveImage = (index: number, direction: -1 | 1) => {
        setEditing((d) => {
            if (!d) return d;
            const target = index + direction;
            if (target < 0 || target >= d.imageUrls.length) return d;
            const next = [...d.imageUrls];
            [next[index], next[target]] = [next[target], next[index]];
            return { ...d, imageUrls: next };
        });
    };

    const addUrl = () => {
        setEditing((d) => (d ? { ...d, urls: [...d.urls, ''] } : d));
    };

    const updateUrl = (index: number, value: string) => {
        setEditing((d) => {
            if (!d) return d;
            const next = [...d.urls];
            next[index] = value;
            return { ...d, urls: next };
        });
    };

    const removeUrl = (index: number) => {
        setEditing((d) =>
            d ? { ...d, urls: d.urls.filter((_, i) => i !== index) } : d,
        );
    };

    if (editing) {
        return (
            <div className="admin-form">
                <h2>{editing.id ? 'Edit work' : 'New work'}</h2>
                <label>
                    <span>Slug</span>
                    <input
                        value={editing.slug ?? ''}
                        onChange={(e) => setEditing({ ...editing, slug: e.target.value })}
                    />
                </label>
                <label>
                    <span>Title</span>
                    <input
                        value={editing.title ?? ''}
                        onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                    />
                </label>
                <label>
                    <span>Date</span>
                    <input
                        type="date"
                        value={editing.date ?? ''}
                        onChange={(e) => setEditing({ ...editing, date: e.target.value })}
                    />
                </label>
                <div className="admin-field">
                    <span className="admin-field-label">Tags</span>
                    <TagInput
                        value={editing.tags}
                        onChange={(next) => setEditing({ ...editing, tags: next })}
                        suggestions={allTags}
                    />
                </div>
                <label>
                    <span>Description</span>
                    <textarea
                        rows={6}
                        value={editing.description ?? ''}
                        onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                    />
                </label>

                <div className="admin-field">
                    <span className="admin-field-label">
                        Images <span className="admin-field-hint">(first one is the hero)</span>
                    </span>
                    <div className="admin-image-grid">
                        {editing.imageUrls.map((url, i) => (
                            <div className="admin-image-thumb" key={`${url}-${i}`}>
                                <img src={url} alt="" />
                                {i === 0 && <span className="admin-image-badge">hero</span>}
                                <div className="admin-image-actions">
                                    {i > 0 && (
                                        <button
                                            type="button"
                                            onClick={() => moveImage(i, -1)}
                                            aria-label="Move left"
                                            title="Move left"
                                        >‹</button>
                                    )}
                                    {i < editing.imageUrls.length - 1 && (
                                        <button
                                            type="button"
                                            onClick={() => moveImage(i, 1)}
                                            aria-label="Move right"
                                            title="Move right"
                                        >›</button>
                                    )}
                                    <button
                                        type="button"
                                        className="admin-image-remove"
                                        onClick={() => removeImage(i)}
                                        aria-label="Remove image"
                                        title="Remove image"
                                    >×</button>
                                </div>
                            </div>
                        ))}
                        <label className="admin-image-upload">
                            <span>{busy ? 'Uploading…' : '+ Upload'}</span>
                            <input
                                type="file"
                                accept="image/*"
                                disabled={busy}
                                onChange={(e) => {
                                    const f = e.target.files?.[0];
                                    if (f) uploadImage(f);
                                    e.target.value = '';
                                }}
                            />
                        </label>
                    </div>
                </div>

                <div className="admin-field">
                    <span className="admin-field-label">
                        URLs <span className="admin-field-hint">(links or embed iframes)</span>
                    </span>
                    <div className="admin-url-list">
                        {editing.urls.map((url, i) => (
                            <div className="admin-url-row" key={i}>
                                <input
                                    value={url}
                                    onChange={(e) => updateUrl(i, e.target.value)}
                                    placeholder="https://… or <iframe …></iframe>"
                                />
                                <button
                                    type="button"
                                    onClick={() => removeUrl(i)}
                                    aria-label="Remove URL"
                                    title="Remove"
                                >×</button>
                            </div>
                        ))}
                        <button type="button" className="admin-add-button" onClick={addUrl}>
                            + Add URL
                        </button>
                    </div>
                </div>

                <label className="admin-pin-toggle">
                    <input
                        type="checkbox"
                        checked={typeof editing.pinnedOrder === 'number'}
                        onChange={(e) =>
                            setEditing({
                                ...editing,
                                pinnedOrder: e.target.checked ? 1 : null,
                            })
                        }
                    />
                    <span>Pinned to top</span>
                </label>
                {error && <div className="admin-error">{error}</div>}
                <div className="admin-form-actions">
                    <button className="admin-primary" onClick={save} disabled={busy}>
                        {busy ? 'Saving…' : 'Save'}
                    </button>
                    <button onClick={closeEditor} disabled={busy}>
                        Cancel
                    </button>
                    {editing.id && (
                        <button className="admin-danger" onClick={remove} disabled={busy}>
                            Delete
                        </button>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="admin-list">
            <div className="admin-list-head">
                <h2>Works ({works.length})</h2>
                <button onClick={openNewEditor}>+ New work</button>
            </div>
            {error && <div className="admin-error">{error}</div>}
            <ul>
                {displayedWorks.map((w) => (
                    <li key={w.id}>
                        <button
                            className="admin-list-row"
                            onClick={() => openEditor(w)}
                        >
                            <span className="admin-list-title">
                                {typeof w.pinnedOrder === 'number' && (
                                    <span className="admin-list-pin" title="Pinned">
                                        <Icon icon={FaThumbtack} />
                                    </span>
                                )}
                                {w.title}
                            </span>
                            <span className="admin-list-meta">
                                {w.date} · {w.tags.join(', ')}
                            </span>
                        </button>
                    </li>
                ))}
            </ul>
        </div>
    );
}
